import React, { useEffect, useRef } from 'react'
import { X, Check, AlertTriangle, ChevronRight } from 'lucide-react'
import { cn } from '@/utils/cn'
import { Detection } from '@/types'
import { useCategoryContext } from '@/contexts/CategoryContext'
import { getClassColor } from '@/utils/colorUtils'
import AuthenticatedImage from '@/components/AuthenticatedImage'

interface MobileDetectionDetailsProps {
  detection: Detection
  cropUrl?: string
  validationStatus?: 'match' | 'mismatch' | 'unexpected'
  isAdmin?: boolean
  onCorrect: (newClass: string) => void
  onClose: () => void
}

const MobileDetectionDetails: React.FC<MobileDetectionDetailsProps> = ({
  detection,
  cropUrl,
  validationStatus,
  isAdmin = false,
  onCorrect,
  onClose,
}) => {
  const { getClassThumbnail, getClassCategory, refreshThumbnailForClass } = useCategoryContext()
  const topClasses = detection.top_classes || []
  const currentClass = detection.user_corrected_class || topClasses[0]?.label || 'Unknown'
  const confidence = topClasses[0]?.score || topClasses[0]?.distance || 0
  const isUserCorrected = detection.is_user_corrected

  // Track which classes we've attempted to load thumbnails for
  const attemptedLoads = useRef<Set<string>>(new Set())

  // Get confidence display
  const confidencePercent = typeof confidence === 'number'
    ? confidence < 1
      ? Math.round(confidence * 100)
      : Math.round(confidence)
    : 0

  // Get thumbnail for a class
  const getThumb = (className: string) => {
    const thumb = getClassThumbnail(className)
    return thumb?.url || null
  }

  // Load thumbnails on-demand for classes that don't have them cached
  useEffect(() => {
    const classNames = [currentClass, ...topClasses.slice(1).map(c => c.label || c.class_name || '')]
      .filter(name => name && name !== 'Unknown')

    classNames.forEach(className => {
      const thumb = getClassThumbnail(className)
      if (!thumb && !attemptedLoads.current.has(className)) {
        attemptedLoads.current.add(className)
        refreshThumbnailForClass(className)
      }
    })
  }, [currentClass, topClasses, getClassThumbnail, refreshThumbnailForClass])

  // Get color for a class
  const getColor = (className: string) => {
    const category = getClassCategory(className)
    return getClassColor(className, category)
  }

  const currentThumb = getThumb(currentClass)
  const currentColor = getColor(currentClass)

  return (
    <div className="fixed inset-0 z-50 bg-white flex flex-col safe-area-inset">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b bg-white flex-shrink-0">
        <h2 className="text-lg font-semibold text-gray-900">Detection Details</h2>
        <button
          onClick={onClose}
          className="p-2 -mr-2 text-gray-500 hover:text-gray-700 active:bg-gray-100 rounded-lg"
        >
          <X className="h-6 w-6" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {/* Detection Crop */}
        {cropUrl && (
          <div className="p-4 bg-gray-100">
            <div className="aspect-square max-w-[200px] mx-auto rounded-lg overflow-hidden bg-white shadow">
              <img
                src={cropUrl}
                alt="Detection crop"
                className="w-full h-full object-contain"
              />
            </div>
          </div>
        )}

        {/* Current Classification */}
        <div className="px-4 py-4 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              {/* Thumbnail or colored fallback */}
              {currentThumb ? (
                <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                  <AuthenticatedImage
                    imageUrl={currentThumb}
                    alt={currentClass}
                    size="thumb"
                    className="w-full h-full object-cover"
                  />
                </div>
              ) : (
                <div
                  className="w-12 h-12 rounded-lg flex-shrink-0 flex items-center justify-center"
                  style={{ backgroundColor: `${currentColor}20` }}
                >
                  <div
                    className="w-5 h-5 rounded-full"
                    style={{ backgroundColor: currentColor }}
                  />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-500 mb-0.5">Current Classification</p>
                <p
                  className="text-base font-semibold truncate"
                  style={{ color: currentColor }}
                >
                  {currentClass}
                </p>
                <div className="flex items-center gap-2 mt-0.5">
                  {/* Only show confidence to admin in purple */}
                  {isAdmin && (
                    <span className="text-sm text-purple-600 font-medium">
                      {confidencePercent}%
                    </span>
                  )}
                  {isUserCorrected && (
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                      Corrected
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Validation Status */}
            {validationStatus && (
              <div
                className={cn(
                  'flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium flex-shrink-0',
                  validationStatus === 'match'
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'bg-amber-100 text-amber-700'
                )}
              >
                {validationStatus === 'match' ? (
                  <>
                    <Check className="h-4 w-4" />
                    Match
                  </>
                ) : (
                  <>
                    <AlertTriangle className="h-4 w-4" />
                    {validationStatus === 'unexpected' ? 'Unexpected' : 'Mismatch'}
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Alternative Classifications */}
        <div className="px-4 py-4">
          <p className="text-sm text-gray-500 mb-3">
            {topClasses.length > 1 ? 'Tap to correct to:' : 'No alternatives available'}
          </p>

          <div className="space-y-2">
            {topClasses.slice(1).map((cls, index) => {
              const label = cls.label || cls.class_name || 'Unknown'
              const score = cls.score || cls.distance || 0
              const scorePercent = score < 1 ? Math.round(score * 100) : Math.round(score)
              const thumb = getThumb(label)
              const color = getColor(label)

              return (
                <button
                  key={index}
                  onClick={() => onCorrect(label)}
                  className={cn(
                    'w-full flex items-center gap-3 p-3 rounded-xl',
                    'bg-gray-50 border border-gray-200',
                    'active:bg-gray-100 transition-colors'
                  )}
                >
                  {/* Thumbnail or colored fallback */}
                  {thumb ? (
                    <div className="w-10 h-10 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                      <AuthenticatedImage
                        imageUrl={thumb}
                        alt={label}
                        size="thumb"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ) : (
                    <div
                      className="w-10 h-10 rounded-lg flex-shrink-0 flex items-center justify-center"
                      style={{ backgroundColor: `${color}20` }}
                    >
                      <div
                        className="w-4 h-4 rounded-full"
                        style={{ backgroundColor: color }}
                      />
                    </div>
                  )}
                  <div className="flex-1 text-left min-w-0">
                    <p
                      className="font-medium truncate"
                      style={{ color }}
                    >
                      {label}
                    </p>
                    {/* Only show confidence to admin in purple */}
                    {isAdmin && (
                      <p className="text-sm text-purple-600">{scorePercent}%</p>
                    )}
                  </div>
                  <ChevronRight className="h-5 w-5 text-gray-400 flex-shrink-0" />
                </button>
              )
            })}
          </div>

          {topClasses.length <= 1 && (
            <div className="text-center py-8 text-gray-500">
              <p className="text-sm">
                No alternative classifications available.
              </p>
              <p className="text-xs mt-1">
                If this detection is incorrect, please correct it from the desktop version.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="px-4 py-4 border-t bg-gray-50 safe-area-bottom flex-shrink-0">
        <button
          onClick={onClose}
          className={cn(
            'w-full py-3.5 rounded-xl font-medium',
            'bg-gray-200 text-gray-700',
            'active:bg-gray-300 transition-colors'
          )}
        >
          Done
        </button>
      </div>
    </div>
  )
}

export default MobileDetectionDetails
